import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";

/** Small animated checkmark — same style as EarlyAccessModal AnimatedCheck, scaled down */
function PillCheck() {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "hsl(var(--success) / 0.15)",
        border: "1px solid hsl(var(--success) / 0.30)",
        flexShrink: 0,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 28 28" fill="none">
        <motion.path
          d="M7 14.5L11.5 19L21 9.5"
          stroke="hsl(var(--success))"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.35, delay: 0.15, ease: "easeOut" }}
        />
      </svg>
    </div>
  );
}

const STORAGE_KEY = "altair_pulse_subscribed";

async function subscribeNewsletter(email: string): Promise<void> {
  try {
    await fetch("/api/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch {
    // Fail silently — don't block the UI if CRM sync fails
  }
}

function isAlreadySubscribed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function markSubscribed(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable — fail silently
  }
}

/**
 * Floating subscribe pill — appears after scrolling, dismissible,
 * and remembers subscription via localStorage so it doesn't show again.
 *
 * Used on both InsightsListPage and InsightDetailPage.
 */
export function FloatingSubscribe() {
  const [shouldShow, setShouldShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(true);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setAlreadySubscribed(isAlreadySubscribed());
  }, []);

  useEffect(() => {
    if (dismissed || alreadySubscribed) return;
    function handleScroll() {
      setShouldShow(window.scrollY > 200);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [dismissed, alreadySubscribed]);

  if (alreadySubscribed) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    subscribeNewsletter(email.trim());
    markSubscribed();
    setSubmitted(true);
    setTimeout(() => {
      setDismissed(true);
      setAlreadySubscribed(true);
    }, 2500);
  }

  function handleDismiss() {
    setDismissed(true);
  }

  return (
    <AnimatePresence>
      {shouldShow && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed left-0 right-0 z-40 flex justify-center pointer-events-none px-4 sm:px-0"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
          }}
        >
          <div
            className="bg-foreground rounded-full inline-flex items-center justify-center pointer-events-auto overflow-hidden w-full sm:w-auto max-w-[520px]"
            style={{
              boxShadow:
                "0 8px 32px -4px hsl(222 47% 11% / 0.20), 0 2px 8px -2px hsl(222 47% 11% / 0.10)",
              gap: 10,
              padding: submitted ? "12px 28px" : "8px 8px 8px 16px",
              transition: "padding 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {!submitted && (
              <span
                className="hidden sm:inline shrink-0"
                style={{
                  fontSize: 14,
                  color: "hsl(var(--background) / 0.80)",
                  whiteSpace: "nowrap",
                }}
              >
                Get the daily Pulse
              </span>
            )}
            {!submitted && (
              <form
                onSubmit={handleSubmit}
                className="flex items-center gap-1.5 flex-1 sm:flex-initial min-w-0"
              >
                <input
                  type="email"
                  placeholder="you@practice.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-label="Email for daily Pulse"
                  className="flex-1 sm:flex-initial sm:w-[180px] min-w-0"
                  style={{
                    fontSize: 13,
                    padding: "7px 14px",
                    borderRadius: 9999,
                    border: "none",
                    background: "hsl(var(--background) / 0.15)",
                    color: "hsl(var(--background))",
                    outline: "none",
                  }}
                />
                <button
                  type="submit"
                  className="shrink-0"
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "7px 14px",
                    borderRadius: 9999,
                    border: "none",
                    background: "hsl(var(--background))",
                    color: "hsl(var(--foreground))",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Subscribe
                </button>
              </form>
            )}
            {!submitted && (
              <button
                onClick={handleDismiss}
                className="shrink-0"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "hsl(var(--background) / 0.50)",
                  padding: 4,
                  display: "flex",
                }}
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            )}
            {submitted && (
              <div className="flex items-center gap-3">
                <PillCheck />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "hsl(var(--background))",
                    whiteSpace: "nowrap",
                  }}
                >
                  Subscribed
                </span>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
